export interface EmptyCardContent {
  title: string;
  description: string;
  externalDescription?: string;
}

export const EMPTY_CARD_CONTENT = {
  managementFees: {
    title: "No Management Fees",
    description: "There are no management fees yet. Add a fee to start tracking management fee data.",
    externalDescription:
      "There are no management fees yet. Reach out to your Altius representative to add management fee data."
  },
  dealReport: {
    title: "No reports yet",
    description: "Generate reports to analyze the performance and key metrics of this deal.",
    externalDescription:
      "There are no reports yet. Reach out to your Altius representative to generate reports for this deal."
  },
  lookThrough: {
    title: "Explore underlying investments",
    description:
      "See the companies and assets behind your fund investments. Add deal data to start tracking where your capital is ultimately deployed.",
    externalDescription:
      "See the companies and assets behind your fund investments. Reach out to your Altius representative to start tracking where your capital is ultimately deployed."
  },
  dataRooms: {
    title: "Connect your data room",
    description:
      "Link your cloud storage to access and manage deal documents directly from the platform. Connect a data room to start syncing files.",
    externalDescription:
      "Link your cloud storage to access and manage deal documents directly from the platform. Reach out to your Altius representative to connect a data room."
  },
  dataRoomsAllFiles: {
    title: "Get Started",
    description:
      "No investor portals are currently connected. Reach out to your Altius representative to connect your portals.",
    externalDescription:
      "No investor portals are currently connected. Reach out to your Altius representative to connect your portals."
  },
  myDealsDashboard: {
    title: "Your portfolio overview starts here",
    description:
      "Track key portfolio metrics and performance indicators in one place. Add deals to begin monitoring allocation, performance, and exposure.",
    externalDescription:
      "Track key portfolio metrics and performance indicators in one place. Reach out to your Altius representative to begin monitoring allocation, performance, and exposure."
  },
  myDealsPipeline: {
    title: "Track your deal pipeline",
    description:
      "Manage investment opportunities across stages. Add deals and move them through the pipeline from initial review to investment decision.",
    externalDescription:
      "Manage investment opportunities across stages. Reach out to your Altius representative to add deals to the pipeline."
  },
  myDealsGrid: {
    title: "See your deals at a glance",
    description:
      "Use the card view to quickly scan key details and status of your deals. Add a deal to start building your investment pipeline.",
    externalDescription:
      "Use the card view to quickly scan key details and status of your deals. Reach out to your Altius representative to start building your investment pipeline."
  },
  myDealsTable: {
    title: "Your deals will appear here",
    description:
      "View and manage all your deals in one place. Add a deal to start tracking its details, documents, and investment status.",
    externalDescription:
      "View and manage all your deals in one place. Reach out to your Altius representative to start tracking deal details, documents, and investment status."
  },
  draftsTable: {
    title: "Start tracking deal cash flows",
    description:
      "Add financial data such as commitments, capital calls, and distributions. Keep all investment activity organized and up to date.",
    externalDescription:
      "Track commitments, capital calls, and distributions. Reach out to your Altius representative to add financial data."
  },
  dataOperationsData: {
    title: "Welcome to your data storage",
    description: "Manage all your files in one place. Classify, extract data, and link it to a specific deal.",
    externalDescription:
      "Manage all your files in one place. Reach out to your Altius representative to classify, extract data, and link files to deals."
  },
  dataOperationsDeleted: {
    title: "No deleted items",
    description: "There are no deleted data entries. Deleted items will appear here.",
    externalDescription: "There are no deleted data entries. Deleted items will appear here."
  },
  workspaceFolder: {
    title: "Your folder is empty",
    description: "This folder is empty. Upload files, create documents, or add new folders to organize your workspace.",
    externalDescription:
      "This folder is empty. Reach out to your Altius representative to upload files and organize your workspace."
  },
  workspaceEmpty: {
    title: "Your deal workspace is empty",
    description:
      "Store and organize all files related to this deal in one place. Upload documents, create folders, and keep your investment materials structured.",
    externalDescription:
      "Store and organize all files related to this deal in one place. Reach out to your Altius representative to upload documents and organize investment materials."
  },
  workspaceGlobalFiles: {
    title: "Your workspace is empty",
    description:
      "All files across your deals will appear here. Upload files to your deal workspaces to see them in this view.",
    externalDescription:
      "All files across your deals will appear here. Reach out to your Altius representative to upload files to deal workspaces."
  },
  plaid: {
    title: "Connect to your bank feed",
    description: "Use Plaid to connect to your bank data.",
    externalDescription: "Reach out to your Altius representative to connect your bank data."
  },
  investorPortfolio: {
    title: "Start building your portfolio strategy",
    description:
      "Use the simulator to model different allocation scenarios and optimize your portfolio structure. Add assets to begin testing portfolio strategies.",
    externalDescription:
      "Use the simulator to model different allocation scenarios and optimize your portfolio structure. Reach out to your Altius representative to add assets and begin testing portfolio strategies."
  },
  measureDataTable: {
    title: "Start tracking deal cash flows",
    description:
      "Add financial data such as commitments, capital calls, and distributions. Keep all investment activity organized and up to date.",
    externalDescription:
      "Track commitments, capital calls, and distributions. Reach out to your Altius representative to add financial data."
  },
  securitiesTable: {
    title: "Manage your securities",
    description:
      "Track equity holdings, ownership details, and changes in stock positions. Connect Altius directly to your bank for securities data. Contact your Altius representative for more details.",
    externalDescription:
      "Track equity holdings, ownership details, and changes in stock positions. Reach out to your Altius representative to connect Altius directly to your bank for securities data."
  },
  expectedPayments: {
    title: "Debt payments will appear here",
    description:
      "Track scheduled and completed debt payments related to investments. Add payment details to monitor obligations and timelines.",
    externalDescription:
      "Track scheduled and completed debt payments related to investments. Reach out to your Altius representative to add payment details."
  },
  combinedAmortization: {
    title: "Debt payments will appear here",
    description:
      "Track scheduled and completed debt payments related to investments. Add payment details to monitor obligations and timelines.",
    externalDescription:
      "Track scheduled and completed debt payments related to investments. Reach out to your Altius representative to add payment details."
  },
  fileRepository: {
    title: "Welcome to your file storage",
    description: "Manage all your files in one place. Classify, extract data, and link it to a specific deal.",
    externalDescription:
      "Manage all your files in one place. Reach out to your Altius representative to classify, extract data, and link files to deals."
  },
  consolidationMeasures: {
    title: "Manage your securities",
    description:
      "Track equity holdings, ownership details, and changes in stock positions. Connect Altius directly to your bank for securities data. Contact your Altius representative for more details.",
    externalDescription:
      "Track equity holdings, ownership details, and changes in stock positions. Reach out to your Altius representative to connect Altius directly to your bank for securities data."
  },
  consolidationAssetClasses: {
    title: "Manage your securities",
    description:
      "Track equity holdings, ownership details, and changes in stock positions. Connect Altius directly to your bank for securities data. Contact your Altius representative for more details.",
    externalDescription:
      "Track equity holdings, ownership details, and changes in stock positions. Reach out to your Altius representative to connect Altius directly to your bank for securities data."
  },
  quickView: {
    title: "Start building your Smart Sheet",
    description:
      "Add and organize investment data in a flexible spreadsheet-style view. Create rows and edit values to structure your deal information.",
    externalDescription:
      "Organize investment data in a flexible spreadsheet-style view. Reach out to your Altius representative to add and structure your deal information."
  },
  portfolioTimeline: {
    title: "Portfolio activity timeline will appear here",
    description:
      "Track how your portfolio evolves over time. Add deals and financial data to visualize capital calls, distributions, and key portfolio events.",
    externalDescription:
      "Track how your portfolio evolves over time. Reach out to your Altius representative to add deals and financial data."
  },
  portfolioCashflow: {
    title: "Cash flow projections will appear here",
    description:
      "Forecast upcoming capital calls, distributions, and liquidity needs across your portfolio. Add deal cash flow data to start seeing future projections.",
    externalDescription:
      "Forecast upcoming capital calls, distributions, and liquidity needs across your portfolio. Reach out to your Altius representative to add deal cash flow data."
  },
  portfolioSnapshot: {
    title: "Portfolio insights will appear here",
    description:
      "Analyze your portfolio across performance, allocation, and exposure. Add deals and data to start seeing meaningful insights and trends.",
    externalDescription:
      "Analyze your portfolio across performance, allocation, and exposure. Reach out to your Altius representative to add deals and data."
  },
  portfolioClassic: {
    title: "Portfolio insights will appear here",
    description:
      "Analyze your portfolio across performance, allocation, and exposure. Add deals and data to start seeing meaningful insights and trends.",
    externalDescription:
      "Analyze your portfolio across performance, allocation, and exposure. Reach out to your Altius representative to add deals and data."
  },
  taskManage: {
    title: "Stay on top of your deal tasks",
    description:
      "Track tasks related to this deal, assign responsibilities, and monitor progress in one place. Create your first task to get started.",
    externalDescription:
      "Track tasks related to this deal, assign responsibilities, and monitor progress in one place. Reach out to your Altius representative to create tasks."
  },
  reportMeasuresTable: {
    title: "Track your portfolio performance",
    description:
      "Analyze returns, growth, and key performance metrics across your portfolio. Add deals and financial data to start measuring performance.",
    externalDescription:
      "Analyze returns, growth, and key performance metrics across your portfolio. Add deals and financial data to start measuring performance."
  }
} as const satisfies Record<string, EmptyCardContent>;
